import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterModule, Routes } from '@angular/router';
import { DotCMSEditablePageService } from '@dotcms/angular';
import { DotCMSClientConfig } from '@dotcms/types';
import { combineLatest, distinctUntilChanged, filter, map, of, race, switchMap, take, tap, timeout, timer } from 'rxjs';
import { ConfigService } from 'src/app/extensions/dot-cms-uve/services/dot-cms-uve-config/dot-cms-config.service';

import { FeatureToggleService } from 'ish-core/feature-toggle.module';

import { provideDotCmsImageLoader } from '../configurations/dot-cms-image-loader';
import { DotCmsUveFacade } from '../facades/dot-cms-uve.facade';

const routes: Routes = [
  {
    path: 'page/blog/post/:slug',
    // CUSTOMIZATION: `AppModule` imports this routing module eagerly, so a static `component:`
    // would put the blog page and its content types in the startup bundle. `loadComponent:`
    // defers them to the first blog visit.
    loadComponent: () =>
      import('../content-types/dot-cms-uve-blog/dot-cms-uve-blog-detail/dot-cms-uve-blog-detail.component').then(
        m => m.DotCmsBlogDetailComponent
      ),
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  providers: [
    DotCMSEditablePageService,
    /**
     * CUSTOMIZATION: the app's only live registration point for `IMAGE_LOADER` — `DotCmsUveModule`
     * is imported nowhere. Because `AppModule` imports this module eagerly, the loader lands in
     * the root injector and applies to every `NgOptimizedImage`, which is safe only because it
     * returns non-`/dA/` sources untouched.
     */
    ...provideDotCmsImageLoader(),
    provideAppInitializer(() => {
      const configService = inject(ConfigService);
      const dotCmsUveFacade = inject(DotCmsUveFacade);
      const pageState$ = toObservable(dotCmsUveFacade.pageState);
      const featureToggleService = inject(FeatureToggleService);
      const configLoaded$ = dotCmsUveFacade.serverConfigLoaded$.pipe(
        filter(loaded => loaded),
        take(1)
      );
      const enabled$ = featureToggleService.enabled$('dotCmsUve');

      return new Promise<void>(resolve => {
        combineLatest([enabled$, configLoaded$])
          .pipe(
            timeout(8000),
            distinctUntilChanged(),
            switchMap(([enabled, _]) => {
              if (!enabled) {
                return of(null);
              }
              return configService.loadConfig().pipe(
                tap((config: DotCMSClientConfig) => {
                  configService.setConfig(config);
                }),
                switchMap(() =>
                  race(
                    pageState$.pipe(filter(state => !!state?.pageResponse)),
                    timer(5000).pipe(tap(() => console.warn('PageState timeout, continuing bootstrap')))
                  )
                ),
                filter(() => !!enabled),
                map(() => null)
              );
            }),
            // CUSTOMIZATION: `timeout(8000)` re-arms after every emission and `enabled$` never
            // completes, so without `take(1)` an 8 s timer outlives `resolve()` and holds SSR
            // serialization until it fires — every server render took `/configurations` + 8 s.
            take(1)
          )
          .subscribe({
            next: () => resolve(),
            error: () => resolve(),
            complete: () => resolve(),
          });
      });
    }),
  ],
})
export class DotCmsUveRoutingModule {}
